import React, { useContext, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
// eslint-disable-next-line import/no-extraneous-dependencies
import PhoneNumberInput, { getCountryCallingCode } from 'react-phone-number-input';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'react-phone-number-input/style.css';

import usePortalSettings from '../../hooks/usePortalSettings';
import DataContext from '../../store/dataContext';

import './style.scss';

const BillingPhoneWithDDI = ({
  onFocus,
  placeholder,
  placeholderTop,
  classNameInput,
  validatePhoneNumber,
  disabled,
  error,
  country,
  setCountry,
  code,
  setCode,
}) => {
  const { billingPhone, updateBillingPhone, apiEmailResponse } = useContext(DataContext);
  const [phone, setPhone] = useState(billingPhone || '+1');
  const [isNotEmpty, setIsNotEmpty] = useState('');

  const [phoneObj, setPhoneObj] = useState({});
  const [preventPropagation, setPreventPropagation] = useState(false);
  const { portalSettings } = usePortalSettings();
  const input = useRef(null);
  const previousValue = useRef('');

  const handleSuggestNumber = number => {
    if (number?.length === 10 && !number.includes('+')) {
      const usNumber = `+1${number}`;
      updateBillingPhone(usNumber);
      setPhone(usNumber);
    } else {
      const internationalPhone = `${number.includes('+') ? '' : '+'}${number}`;
      updateBillingPhone(internationalPhone);
      setPhone(internationalPhone);
    }
  };

  useEffect(() => {
    const suggestionPhone = apiEmailResponse?.suggestions?.autofill?.billPhone;
    if (suggestionPhone && suggestionPhone !== billingPhone) {
      handleSuggestNumber(suggestionPhone);
    }
  }, [apiEmailResponse]);

  useEffect(() => {
    const isAutocomplete = Math.abs(phone?.length - (previousValue.current?.length || 0)) > 5;
    if (
      disabled ||
      previousValue.current === phone ||
      previousValue.current === `+1${phone?.substring(1)}` ||
      phone === '+1'
    ) {
      return;
    }

    if (input.current) {
      const splitPhone = String(input.current.value).split(' ');

      if (splitPhone.length > 2) {
        const [ddi, ddd, ...rest] = splitPhone;
        const ddiValue = String(isAutocomplete ? '1' : code || ddi.replace(/\+/g, ''));
        setPhoneObj({
          ddi: ddiValue,
          ddd,
          phone: rest.join(''),
          completeNumber: ddiValue.concat(ddd).concat(rest.join('')),
        });
      } else {
        const [ddi, ...rest] = splitPhone;
        const ddiValue = String(isAutocomplete ? '1' : code || ddi.replace(/\+/g, ''));
        setPhoneObj({
          ddi: ddiValue,
          ddd: '',
          phone: rest.join(''),
          completeNumber: ddiValue.concat(rest.join('')),
        });
      }
      previousValue.current = phone;
    }
  }, [phone]);

  const handleOnChange = async value => {
    if (preventPropagation) return;
    let newValue = value;
    const isAutocomplete = Math.abs(value?.length - (previousValue.current?.length || 0)) > 5;
    if (isAutocomplete) {
      if (value?.indexOf('+1') !== 0) {
        newValue = `+1${value.substring(1)}`;
      }
    }
    setPhone(newValue);
    setPreventPropagation(true);
  };

  const handleOnChangeCountry = c => {
    if (c) {
      const ddi = getCountryCallingCode(c);
      setCode(ddi);
      setCountry(c);
    } else {
      setCode('');
    }
  };

  useEffect(() => {
    if (code) {
      setPhoneObj(old => ({
        ...old,
        ddi: code,
      }));
    }
  }, [code]);

  const handleFocus = e => {
    onFocus(e);
    setIsNotEmpty('is-value');
  };

  useEffect(() => {
    const phoneNum = `${phoneObj.ddi} ${phoneObj.ddd} ${phoneObj.phone}`;
    if (phoneObj?.ddi?.length === 1 && phoneObj?.ddd?.length === 1 && phoneObj?.phone?.length === 1) {
      updateBillingPhone('+1');
    } else if (phoneObj.ddi && billingPhone !== phoneNum) {
      updateBillingPhone(phoneNum);
    }
    setPreventPropagation(false);
  }, [phoneObj]);

  useEffect(() => {
    if (!disabled && !phone && !billingPhone) {
      setPhone('+1');
      updateBillingPhone('+1');
    }
    if (!disabled && !phone) {
      setPhone(billingPhone);
    }
  }, [disabled]);

  useEffect(() => {
    if (phone) {
      setIsNotEmpty('is-value');
    }
  }, []);

  const handleBlur = () => {
    setPhone(input.current.value);
    updateBillingPhone(input.current.value);
  };

  useEffect(() => {
    if (billingPhone === phone && billingPhone !== '+1') {
      validatePhoneNumber();
    }
  }, [billingPhone]);

  return (
    <div tabIndex="-1" className="phone-input-wrapper">
      {disabled && <div className="nf-disabled-but-clickable" />}
      <PhoneNumberInput
        className={`${classNameInput} ${isNotEmpty} phone-with-ddi`}
        ref={input}
        id="phone-with-ddi"
        iscode={code}
        value={disabled ? '' : phone}
        onChange={handleOnChange}
        onCountryChange={handleOnChangeCountry}
        country={country}
        international
        onFocus={handleFocus}
        onBlur={handleBlur}
        defaultCountry="US"
        disabled={disabled}
      />
      <label tabIndex="-1" htmlFor="phone-with-ddi">
        <span
          tabIndex="-1"
          className={classNames({
            placeholder: true,
          })}
        >
          {placeholder}
        </span>
        <span tabIndex="-1" className="placeholder-top" style={{ color: portalSettings.theme?.mainColor }}>
          {placeholderTop}
        </span>
      </label>
      {error && <div className="error">{error}</div>}
    </div>
  );
};

BillingPhoneWithDDI.propTypes = {
  placeholder: PropTypes.string,
  placeholderTop: PropTypes.string,
  classNameInput: PropTypes.string,
  validatePhoneNumber: PropTypes.func,
  onFocus: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  code: PropTypes.string,
  setCode: PropTypes.func,
  country: PropTypes.func,
  setCountry: PropTypes.func,
};

export default BillingPhoneWithDDI;
